import { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, ChevronRight, ArrowLeft } from 'lucide-react';
import { api, type EventItem } from '../api';
import { PageLoader } from '../components/ui';
import { SmartCoverMedia } from '../components/SmartCoverMedia';
import { FormattedText, plainTextFromMarkup } from '../lib/simpleMarkup';

const PAGE_SIZE = 10;

type EventLocationState = { from?: string };

function teaser(body: string, max = 140): string {
  const clean = plainTextFromMarkup(body);
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max).replace(/\s+\S*$/, '')}…`;
}

function eventCardClass(isRead: boolean | undefined): string {
  // Unread (students only): solid sand-soft — opacity modifiers like bg-sand/10
  // do not work with CSS-variable color tokens.
  if (isRead === false) {
    return 'group block rounded-2xl border border-sand bg-sand-soft overflow-hidden hover:shadow-pop transition-all touch-manipulation';
  }
  return 'group block bg-surface rounded-2xl border border-line overflow-hidden hover:shadow-pop transition-all touch-manipulation';
}

export function EventsPage() {
  const location = useLocation();
  const fromClub = (location.state as EventLocationState | null)?.from === '/student';

  const q = useInfiniteQuery({
    queryKey: ['events'],
    queryFn: ({ pageParam }) =>
      api.get<{ events: EventItem[]; total: number; unreadCount?: number }>(
        `/api/events?page=${pageParam}&limit=${PAGE_SIZE}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.events.length, 0);
      return loaded < last.total ? pages.length + 1 : undefined;
    },
  });

  if (q.isLoading) return <PageLoader />;

  if (q.isError) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">Could not load events</h1>
        <p className="text-ink-soft text-sm mb-4">Check your connection and try again.</p>
        <button
          type="button"
          onClick={() => q.refetch()}
          className="inline-flex items-center justify-center bg-sand text-white px-5 py-3 rounded-radius-sm text-sm font-semibold min-h-[48px]"
        >
          Try again
        </button>
      </div>
    );
  }

  const events = q.data?.pages.flatMap(p => p.events) ?? [];
  const hasMore = !!q.hasNextPage;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
      <div className="mb-6 sm:mb-8">
        {fromClub && (
          <Link
            to="/student"
            className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink mb-3"
          >
            <ArrowLeft className="w-4 h-4" /> Back to club
          </Link>
        )}
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-2">Events</h1>
        <p className="text-ink-soft text-sm sm:text-base">
          Workshops and open days with clinics and mentors.
        </p>
      </div>

      {events.length === 0 ? (
        <div className="max-w-lg mx-auto text-center py-16">
          <div className="w-16 h-16 rounded-full bg-teal-soft flex items-center justify-center mx-auto mb-4">
            <CalendarDays className="w-7 h-7 text-teal" />
          </div>
          <h2 className="font-heading text-xl font-bold text-ink mb-2">No events yet</h2>
          <p className="text-ink-soft text-sm">No workshops or open days listed right now.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {events.map(event => (
              <Link
                key={event.id}
                to={`/events/${event.slug}`}
                state={{ from: '/events' } satisfies EventLocationState}
                className={eventCardClass(event.isRead)}
              >
                <div className="relative h-44 bg-teal-soft overflow-hidden">
                  {event.photoUrl ? (
                    <SmartCoverMedia
                      src={event.photoUrl}
                      kind={event.photoKind}
                      alt={event.title}
                      className="w-full h-full"
                      mediaClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      playable={false}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <CalendarDays className="w-10 h-10 text-teal" />
                    </div>
                  )}
                </div>
                <div className="p-4 sm:p-5">
                  <div className="font-semibold text-ink text-base sm:text-lg group-hover:text-teal transition-colors">
                    {event.title}
                  </div>
                  <p className="text-sm text-ink-soft mt-2 line-clamp-3">{teaser(event.body)}</p>
                  <div className="mt-3 text-xs font-medium text-sand-strong inline-flex items-center gap-1">
                    Read more <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {hasMore && (
            <div className="mt-8">
              {q.isFetchingNextPage ? (
                <PageLoader variant="inline" />
              ) : (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => void q.fetchNextPage()}
                    className="inline-flex items-center justify-center min-h-[48px] px-6 rounded-radius-sm bg-sand text-white text-sm font-semibold touch-manipulation"
                  >
                    Load more
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function EventDetailPage() {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const location = useLocation();
  const qc = useQueryClient();
  const from = (location.state as EventLocationState | null)?.from;
  const backTo = from === '/student' ? '/student' : '/events';
  const backLabel = from === '/student' ? 'Back to club' : 'All events';

  const q = useQuery({
    queryKey: ['event', idOrSlug],
    queryFn: () => api.get<{ event: EventItem }>(`/api/events/${idOrSlug}`),
    enabled: !!idOrSlug,
  });

  useEffect(() => {
    if (!q.isSuccess) return;
    void qc.invalidateQueries({ queryKey: ['events'] });
    void qc.invalidateQueries({ queryKey: ['events-club-notify'] });
  }, [q.isSuccess, q.dataUpdatedAt, qc]);

  if (q.isLoading) return <PageLoader variant="detail" />;
  if (!q.data?.event) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <h1 className="font-heading text-2xl font-bold text-ink mb-2">Event not found</h1>
        <Link to={backTo} className="text-sand-strong text-sm font-medium hover:underline">
          ← {backLabel}
        </Link>
      </div>
    );
  }

  const event = q.data.event;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
      <Link
        to={backTo}
        className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> {backLabel}
      </Link>

      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-4">{event.title}</h1>
      <FormattedText
        text={event.body}
        className="text-ink-soft text-base sm:text-lg"
      />
      {event.photoUrl && (
        <div className="rounded-2xl overflow-hidden h-52 sm:h-72 bg-teal-soft mt-6 sm:mt-8">
          <SmartCoverMedia
            src={event.photoUrl}
            kind={event.photoKind}
            alt={event.title}
            className="w-full h-full"
          />
        </div>
      )}
    </div>
  );
}
